import { FetchError } from "@medusajs/js-sdk"
import { HttpTypes } from "@medusajs/types"
import {
  QueryKey,
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
} from "@tanstack/react-query"
import {
  fetchQuery,
  importProductsQuery,
  ProductImportValidationEnvelope,
  sdk,
  validateProductImportQuery,
} from "../../lib/client"
import { queryClient } from "../../lib/query-client"
import { queryKeysFactory } from "../../lib/query-key-factory"
import {
  checkCategoryMatch,
  checkCollectionMatch,
  checkStatusMatch,
  checkTagMatch,
  checkTypeMatch,
} from "./helpers/productFilters"
import { inventoryItemsQueryKeys } from "./inventory.tsx"
import productsImagesFormatter from "../../utils/products-images-formatter"

const PRODUCTS_QUERY_KEY = "products" as const
export const productsQueryKeys = queryKeysFactory(PRODUCTS_QUERY_KEY)

const VARIANTS_QUERY_KEY = "product_variants" as const
export const variantsQueryKeys = queryKeysFactory(VARIANTS_QUERY_KEY)

const OPTIONS_QUERY_KEY = "product_options" as const
export const optionsQueryKeys = queryKeysFactory(OPTIONS_QUERY_KEY)

export const useCreateProductOption = (
  productId: string,
  options?: UseMutationOptions<any, FetchError, any>
) => {
  return useMutation({
    mutationFn: (payload: HttpTypes.AdminCreateProductOption) =>
      fetchQuery(`/vendor/products/${productId}/options`, {
        method: "POST",
        body: payload,
      }),
    onSuccess: (data: any, variables: any, context: any) => {
      queryClient.invalidateQueries({
        queryKey: optionsQueryKeys.lists(),
      })
      queryClient.invalidateQueries({
        queryKey: productsQueryKeys.detail(productId),
      })
      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

export const useUpdateProductOption = (
  productId: string,
  optionId: string,
  options?: UseMutationOptions<any, FetchError, any>
) => {
  return useMutation({
    mutationFn: (payload: HttpTypes.AdminUpdateProductOption) =>
      fetchQuery(`/vendor/products/${productId}/options/${optionId}`, {
        method: "POST",
        body: payload,
      }),
    onSuccess: (data: any, variables: any, context: any) => {
      queryClient.invalidateQueries({
        queryKey: optionsQueryKeys.lists(),
      })
      queryClient.invalidateQueries({
        queryKey: optionsQueryKeys.detail(optionId),
      })
      queryClient.invalidateQueries({
        queryKey: productsQueryKeys.detail(productId),
      })

      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

export const useDeleteProductOption = (
  productId: string,
  optionId: string,
  options?: UseMutationOptions<any, FetchError, void>
) => {
  return useMutation({
    mutationFn: () =>
      fetchQuery(`/vendor/products/${productId}/options/${optionId}`, {
        method: "DELETE",
      }),
    onSuccess: (data: any, variables: any, context: any) => {
      queryClient.invalidateQueries({
        queryKey: optionsQueryKeys.lists(),
      })
      queryClient.invalidateQueries({
        queryKey: optionsQueryKeys.detail(optionId),
      })
      queryClient.invalidateQueries({
        queryKey: productsQueryKeys.detail(productId),
      })

      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

export const useProductVariant = (
  productId: string,
  variantId: string,
  query?: HttpTypes.AdminProductVariantParams,
  options?: Omit<
    UseQueryOptions<
      HttpTypes.AdminProductVariantResponse,
      FetchError,
      HttpTypes.AdminProductVariantResponse,
      QueryKey
    >,
    "queryFn" | "queryKey"
  >
) => {
  const toProductVariantFields = (fields?: unknown) => {
    const defaultFields =
      "*variants,*variants.inventory,*variants.inventory.location_levels"

    if (typeof fields !== "string" || !fields.trim()) {
      return defaultFields
    }

    const mappedFields = new Set<string>(["*variants"])

    fields
      .split(",")
      .map((field) => field.trim())
      .filter(Boolean)
      .forEach((field) => {
        if (field.startsWith("*variants.") || field.startsWith("variants.")) {
          mappedFields.add(field)
          return
        }

        if (field.startsWith("*")) {
          mappedFields.add(`*variants.${field.slice(1)}`)
          return
        }

        mappedFields.add(`variants.${field}`)
      })

    return Array.from(mappedFields).join(",")
  }

  const { data, ...rest } = useQuery({
    queryFn: async () => {
      const queryParams = {
        ...(query as Record<string, string | number | undefined> | undefined),
        fields: toProductVariantFields((query as { fields?: string })?.fields),
      }

      const { product } = await fetchQuery(`/vendor/products/${productId}`, {
        method: "GET",
        query: queryParams as Record<string, string | number>,
      })

      const variants = Array.isArray(product?.variants) ? product.variants : []

      const variant = variants.find((variant: { id?: string; temp_id?: string }) => {
        return variant.id === variantId || variant.temp_id === variantId
      })

      return { variant }
    },
    queryKey: variantsQueryKeys.detail(variantId, query),
    ...options,
  })

  return { ...data, ...rest }
}

export const useProductVariants = (
  productId: string,
  query?: HttpTypes.AdminProductVariantParams,
  options?: Omit<
    UseQueryOptions<
      HttpTypes.AdminProductVariantListResponse,
      FetchError,
      HttpTypes.AdminProductVariantListResponse,
      QueryKey
    >,
    "queryFn" | "queryKey"
  >
) => {
  const { data, ...rest } = useQuery({
    queryFn: () => sdk.admin.product.listVariants(productId, query),
    queryKey: variantsQueryKeys.list({
      productId,
      ...query,
    }),
    ...options,
  })

  return { ...data, ...rest }
}

export const useCreateProductVariant = (
  productId: string,
  options?: UseMutationOptions<any, FetchError, any>
) => {
  return useMutation({
    mutationFn: (payload: HttpTypes.AdminCreateProductVariant) =>
      fetchQuery(`/vendor/products/${productId}/variants`, {
        method: "POST",
        body: payload,
      }),
    onSuccess: (data: any, variables: any, context: any) => {
      queryClient.invalidateQueries({
        queryKey: variantsQueryKeys.lists(),
      })
      queryClient.invalidateQueries({
        queryKey: productsQueryKeys.detail(productId),
      })
      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

export const useUpdateProductVariant = (
  productId: string,
  variantId: string,
  options?: UseMutationOptions<any, FetchError, any>
) => {
  return useMutation({
    mutationFn: (body: HttpTypes.AdminUpdateProductVariant) =>
      fetchQuery(`/vendor/products/${productId}/variants/${variantId}`, {
        method: "POST",
        body,
      }),
    onSuccess: (data: any, variables: any, context: any) => {
      queryClient.invalidateQueries({
        queryKey: variantsQueryKeys.lists(),
      })
      queryClient.invalidateQueries({
        queryKey: variantsQueryKeys.detail(variantId),
      })
      queryClient.invalidateQueries({
        queryKey: productsQueryKeys.detail(productId),
      })

      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

export const useUpdateProductVariantsBatch = (
  productId: string,
  options?: UseMutationOptions<any, FetchError, any>
) => {
  return useMutation({
    mutationFn: (
      payload: HttpTypes.AdminBatchProductVariantRequest["update"]
    ) =>
      sdk.admin.product.batchVariants(productId, {
        update: payload,
      }),
    onSuccess: (data: any, variables: any, context: any) => {
      queryClient.invalidateQueries({
        queryKey: variantsQueryKeys.lists(),
      })
      queryClient.invalidateQueries({
        queryKey: variantsQueryKeys.details(),
      })
      queryClient.invalidateQueries({
        queryKey: productsQueryKeys.detail(productId),
      })

      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

export const useProductVariantsInventoryItemsBatch = (
  productId: string,
  options?: UseMutationOptions<
    HttpTypes.AdminBatchProductVariantInventoryItemResponse,
    FetchError,
    HttpTypes.AdminBatchProductVariantInventoryItemRequest
  >
) => {
  return useMutation({
    mutationFn: (payload) =>
      sdk.admin.product.batchVariantInventoryItems(productId, payload),
    onSuccess: (data: any, variables: any, context: any) => {
      queryClient.invalidateQueries({
        queryKey: variantsQueryKeys.lists(),
      })
      queryClient.invalidateQueries({
        queryKey: variantsQueryKeys.details(),
      })
      queryClient.invalidateQueries({
        queryKey: productsQueryKeys.detail(productId),
      })

      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

export const useDeleteVariant = (
  productId: string,
  variantId: string,
  options?: UseMutationOptions<any, FetchError, void>
) => {
  return useMutation({
    mutationFn: () =>
      fetchQuery(`/vendor/products/${productId}/variants/${variantId}`, {
        method: "DELETE",
      }),
    onSuccess: (data: any, variables: any, context: any) => {
      queryClient.invalidateQueries({
        queryKey: variantsQueryKeys.lists(),
      })
      queryClient.invalidateQueries({
        queryKey: variantsQueryKeys.detail(variantId),
      })
      queryClient.invalidateQueries({
        queryKey: productsQueryKeys.detail(productId),
      })

      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

export const useDeleteVariantLazy = (
  productId: string,
  options?: UseMutationOptions<
    HttpTypes.AdminProductVariantDeleteResponse,
    FetchError,
    { variantId: string }
  >
) => {
  return useMutation({
    mutationFn: ({ variantId }) =>
      fetchQuery(`/vendor/products/${productId}/variants/${variantId}`, {
        method: "DELETE",
      }),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: variantsQueryKeys.lists(),
      })
      queryClient.invalidateQueries({
        queryKey: variantsQueryKeys.detail(variables.variantId),
      })
      queryClient.invalidateQueries({
        queryKey: productsQueryKeys.detail(productId),
      })

      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

export const useProductAttributes = (id: string) => {
  const { data, ...rest } = useQuery({
    queryFn: () =>
      fetchQuery(`/vendor/products/${id}/applicable-attributes`, {
        method: "GET",
      }),
    queryKey: ["product", id, "product-attributes"],
  })

  return { ...data, ...rest }
}

export const useProduct = (
  id: string,
  query?: Record<string, any>,
  options?: Omit<
    UseQueryOptions<
      HttpTypes.AdminProductResponse,
      FetchError,
      HttpTypes.AdminProductResponse,
      QueryKey
    >,
    "queryFn" | "queryKey"
  >
) => {
  const normalizeProductFields = (fields?: unknown): string | undefined => {
    if (typeof fields !== "string" || !fields.trim()) {
      return undefined
    }

    const normalizeToken = (token: string) => {
      const trimmed = token.trim()

      if (
        trimmed === "*options.values" ||
        trimmed === "options.values" ||
        trimmed === "+options.values"
      ) {
        return trimmed.replace("options.values", "options.values.*")
      }

      if (
        trimmed === "*variants.options.option.values" ||
        trimmed === "variants.options.option.values" ||
        trimmed === "+variants.options.option.values"
      ) {
        return trimmed.replace(
          "variants.options.option.values",
          "variants.options.option.values.*"
        )
      }

      return trimmed
    }

    const normalized = fields
      .split(",")
      .map(normalizeToken)
      .filter(Boolean)

    return Array.from(new Set(normalized)).join(",")
  }

  const { data, ...rest } = useQuery({
    queryFn: () => {
      const normalizedFields = normalizeProductFields(query?.fields)
      const queryParams = Object.entries({
        ...(query as Record<string, string | number | undefined>),
        ...(normalizedFields ? { fields: normalizedFields } : {}),
      }).reduce(
        (acc, [key, value]) => {
          if (typeof value === "string" || typeof value === "number") {
            acc[key] = value
          }

          return acc
        },
        {} as Record<string, string | number>
      )

      return fetchQuery(`/vendor/products/${id}`, {
        method: "GET",
        query: queryParams,
      })
    },
    queryKey: productsQueryKeys.detail(id, query),
    ...options,
  })

  return {
    ...data,
    product: productsImagesFormatter(data?.product),
    ...rest,
  }
}

export const useProducts = (
  query?: HttpTypes.AdminProductListParams,
  options?: Omit<
    UseQueryOptions<
      HttpTypes.AdminProductListResponse,
      FetchError,
      HttpTypes.AdminProductListResponse,
      QueryKey
    >,
    "queryFn" | "queryKey"
  >,
  filter?: HttpTypes.AdminProductListParams & {
    tagId?: string | string[]
    categoryId?: string | string[]
    collectionId?: string | string[]
    typeId?: string | string[]
    status?: string | string[]
    q?: string
    sort?: string
    id?: string | string[]
    is_giftcard?: boolean
    created_at?: HttpTypes.AdminProductListParams["created_at"]
    updated_at?: HttpTypes.AdminProductListParams["updated_at"]
  }
) => {
  const { data, ...rest } = useQuery({
    queryFn: () =>
      fetchQuery("/vendor/products", {
        method: "GET",
        query: query as Record<string, string | number>,
      }),
    queryKey: productsQueryKeys.list(query),
    ...options,
  })

  let products = data?.products || []

  const matchesDateFilter = (
    value: string | null | undefined,
    filterValue:
      | {
          lt?: string
          gt?: string
          lte?: string
          gte?: string
        }
      | undefined
  ) => {
    if (!filterValue) {
      return true
    }

    if (!value) {
      return false
    }

    const current = new Date(value).getTime()

    if (Number.isNaN(current)) {
      return false
    }

    const checks = [
      filterValue.lt
        ? current < new Date(filterValue.lt).getTime()
        : true,
      filterValue.lte
        ? current <= new Date(filterValue.lte).getTime()
        : true,
      filterValue.gt
        ? current > new Date(filterValue.gt).getTime()
        : true,
      filterValue.gte
        ? current >= new Date(filterValue.gte).getTime()
        : true,
    ]

    return checks.every(Boolean)
  }

  const hasLocalFilters =
    filter?.q ||
    filter?.categoryId ||
    filter?.tagId ||
    filter?.collectionId ||
    filter?.typeId ||
    filter?.status ||
    filter?.id ||
    typeof filter?.is_giftcard === "boolean" ||
    filter?.created_at ||
    filter?.updated_at

  if (hasLocalFilters) {
    products = products.filter((item) => {
      if (
        filter.q &&
        !item.title?.toLowerCase().includes(filter.q.toLowerCase())
      ) {
        return false
      }

      if (filter.categoryId && !checkCategoryMatch(item.categories, filter.categoryId)) {
        return false
      }

      if (filter.tagId && !checkTagMatch(item.tags, filter.tagId)) {
        return false
      }

      if (
        filter.collectionId &&
        !checkCollectionMatch(item.collection, filter.collectionId)
      ) {
        return false
      }

      if (filter.typeId && !checkTypeMatch(item.type_id, filter.typeId)) {
        return false
      }

      if (filter.status && !checkStatusMatch(item.status, filter.status)) {
        return false
      }

      if (filter.id) {
        const ids = Array.isArray(filter.id) ? filter.id : [filter.id]

        if (!ids.includes(item.id)) {
          return false
        }
      }

      if (
        typeof filter.is_giftcard === "boolean" &&
        item.is_giftcard !== filter.is_giftcard
      ) {
        return false
      }

      if (!matchesDateFilter(item.created_at, filter.created_at)) {
        return false
      }

      if (!matchesDateFilter(item.updated_at, filter.updated_at)) {
        return false
      }

      return true
    })
  }

  if (filter?.sort) {
    const isDescending = filter.sort.startsWith("-")
    const field = isDescending ? filter.sort.slice(1) : filter.sort

    if (["title", "created_at", "updated_at"].includes(field)) {
      products = [...products].sort((a, b) => {
        const aValue = a[field as keyof HttpTypes.AdminProduct]
        const bValue = b[field as keyof HttpTypes.AdminProduct]

        if (field === "title") {
          const titleA = String(aValue || "")
          const titleB = String(bValue || "")

          return isDescending
            ? titleB.localeCompare(titleA)
            : titleA.localeCompare(titleB)
        }

        const dateA = new Date((aValue as string) || 0).getTime()
        const dateB = new Date((bValue as string) || 0).getTime()

        return isDescending ? dateB - dateA : dateA - dateB
      })
    }
  }

  return {
    ...data,
    products: productsImagesFormatter(products) || [],
    count: hasLocalFilters ? products.length : data?.count || 0,
    ...rest,
  }
}

export const useCreateProduct = (
  options?: UseMutationOptions<HttpTypes.AdminProductResponse, FetchError, any>
) => {
  return useMutation({
    mutationFn: async (payload) =>
      await fetchQuery("/vendor/products", {
        method: "POST",
        body: payload,
      }),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: productsQueryKeys.lists(),
      })
      queryClient.invalidateQueries({
        queryKey: inventoryItemsQueryKeys.lists(),
      })
      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

export const useUpdateProduct = (
  id: string,
  options?: UseMutationOptions<
    HttpTypes.AdminProductResponse,
    FetchError,
    HttpTypes.AdminUpdateProduct
  >
) => {
  return useMutation({
    mutationFn: (payload) =>
      fetchQuery(`/vendor/products/${id}`, {
        method: "POST",
        body: payload,
      }),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({
        queryKey: productsQueryKeys.lists(),
      })
      await queryClient.invalidateQueries({
        queryKey: productsQueryKeys.detail(id),
      })

      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

type VendorProductStatus = "draft" | "proposed" | "published"

export const useUpdateProductStatus = (
  id: string,
  options?: UseMutationOptions<
    any,
    FetchError,
    {
      status: VendorProductStatus
    }
  >
) => {
  return useMutation({
    mutationFn: (payload) =>
      fetchQuery(`/vendor/products/${id}/status`, {
        method: "POST",
        body: payload,
      }),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({
        queryKey: productsQueryKeys.lists(),
      })
      await queryClient.invalidateQueries({
        queryKey: productsQueryKeys.detail(id),
      })

      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

export const useDeleteProduct = (
  id: string,
  options?: UseMutationOptions<
    HttpTypes.AdminProductDeleteResponse,
    FetchError,
    void
  >
) => {
  return useMutation({
    mutationFn: () =>
      fetchQuery(`/vendor/products/${id}`, {
        method: "DELETE",
      }),
    onSuccess: (data: any, variables: any, context: any) => {
      queryClient.invalidateQueries({
        queryKey: productsQueryKeys.lists(),
      })
      queryClient.invalidateQueries({
        queryKey: productsQueryKeys.detail(id),
      })

      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

export const useExportProducts = (
  query?: HttpTypes.AdminProductListParams,
  options?: UseMutationOptions<
    HttpTypes.AdminExportProductResponse & { url: string },
    FetchError,
    HttpTypes.AdminExportProductRequest
  >
) => {
  return useMutation({
    mutationFn: (payload) =>
      fetchQuery("/vendor/products/export", {
        method: "POST",
        body: payload,
        query: query as { [key: string]: string },
      }),
    onSuccess: (data, variables, context) => {
      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

export const useImportProducts = (
  options?: UseMutationOptions<
    HttpTypes.AdminImportProductResponse,
    FetchError,
    HttpTypes.AdminImportProductRequest
  >
) => {
  return useMutation({
    mutationFn: (payload) => importProductsQuery(payload.file),
    onSuccess: (data, variables, context) => {
      void queryClient.invalidateQueries({
        queryKey: productsQueryKeys.lists(),
      })

      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

export const useValidateProductImport = (
  options?: UseMutationOptions<
    ProductImportValidationEnvelope,
    FetchError,
    { file: File }
  >
) => {
  return useMutation({
    mutationFn: (payload) => validateProductImportQuery(payload.file),
    ...options,
  })
}

export const useConfirmImportProducts = (
  options?: UseMutationOptions<{}, FetchError, string>
) => {
  return useMutation({
    mutationFn: (payload) => sdk.admin.product.confirmImport(payload),
    onSuccess: (data, variables, context) => {
      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

interface Brand {
  id: string
  name: string
  handle?: string
  description?: string
  main_img_url?: string
  additional_img_urls?: string[]
  logo_url?: string
  banner_url?: string
  banner_title?: string
  banner_subtitle?: string
  is_featured?: boolean
  metadata?: Record<string, any>
  created_at?: string
  updated_at?: string
  deleted_at?: string | null
}

interface BrandListParams {
  limit?: number
  offset?: number
  fields?: string[]
  q?: string
  name?: string
  handle?: string
  is_featured?: boolean
  created_at?: {
    lt?: string
    gt?: string
    lte?: string
    gte?: string
  }
  updated_at?: {
    lt?: string
    gt?: string
    lte?: string
    gte?: string
  }
}

interface BrandListResponse {
  brands: Brand[]
  count?: number
  limit?: number
  offset?: number
}

const brandsQueryKeys = {
  all: ["brands"] as const,
  list: (query?: BrandListParams) =>
    [...brandsQueryKeys.all, "list", query] as const,
}

export const useBrands = (
  query?: BrandListParams,
  options?: Omit<
    UseQueryOptions<BrandListResponse, FetchError, BrandListResponse, QueryKey>,
    "queryFn" | "queryKey"
  >
) => {
  const { data, ...rest } = useQuery({
    queryFn: () =>
      fetchQuery("/vendor/brands", {
        method: "GET",
        query: query as Record<string, string | number>,
      }),
    queryKey: brandsQueryKeys.list(query),
    ...options,
  })

  return { ...data, ...rest }
}

interface AssignBrandToProductRequest {
  brand_name: string
}

interface AssignBrandToProductResponse {
  product: any
}

export const useAssignBrandToProduct = (
  productId: string,
  options?: UseMutationOptions<
    AssignBrandToProductResponse,
    FetchError,
    AssignBrandToProductRequest
  >
) => {
  const { onSuccess, ...restOptions } = options ?? {}

  return useMutation({
    ...restOptions,
    mutationFn: (body: AssignBrandToProductRequest) =>
      fetchQuery(`/vendor/products/${productId}/brand`, {
        method: "POST",
        body,
      }),
    onSuccess: (
      data: AssignBrandToProductResponse,
      variables: AssignBrandToProductRequest,
      context: any
    ) => {
      queryClient.invalidateQueries({
        queryKey: productsQueryKeys.detail(productId),
      })
      queryClient.invalidateQueries({
        queryKey: productsQueryKeys.lists(),
      })

      onSuccess?.(data, variables, context)
    },
  })
}

export const useRemoveBrandFromProduct = (
  productId: string,
  options?: UseMutationOptions<
    AssignBrandToProductResponse,
    FetchError,
    void
  >
) => {
  const { onSuccess, ...restOptions } = options ?? {}

  return useMutation({
    ...restOptions,
    mutationFn: () =>
      fetchQuery(`/vendor/products/${productId}/brand`, {
        method: "DELETE",
      }),
    onSuccess: (
      data: AssignBrandToProductResponse,
      variables: void,
      context: any
    ) => {
      queryClient.invalidateQueries({
        queryKey: productsQueryKeys.detail(productId),
      })
      queryClient.invalidateQueries({
        queryKey: productsQueryKeys.lists(),
      })

      onSuccess?.(data, variables, context)
    },
  })
}
